import MojitoCore from 'mojito/core';
import ServicesTypes from 'services/common/types.js';
import { STORE_KEY, INITIAL_STATE } from './types.js';

const reduxInstance = MojitoCore.Services.redux;
const EMPTY_ARRAY = [];
const { UNKNOWN } = ServicesTypes.CONTENT_STATE;

/**
 * Promotions related selectors.
 *
 * @module PromotionsSelectors
 * @name selectors
 * @memberof Mojito.Services.Promotions
 */

/**
 * Selects promotion state.
 *
 * @function selectState
 *
 * @param {object} [state] - Application state object. If not provided then state from global {@link Mojito.Core.Services.redux|redux store} will be returned.
 * @returns {Mojito.Services.Promotions.PromotionsState} Promotions state.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || INITIAL_STATE;
};

/**
 * Selects promotions.
 *
 * @callback PromotionsSelector
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<object>} Promotions list.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectPromotions = state => {
    return selectState(state).promotions;
};

/**
 * Selects all merged promotion containers keyed by container id.
 *
 * @callback PromotionContainersSelector
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {object} Promotion containers map.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectContainers = state => {
    return selectState(state).containers;
};

/**
 * Selects the promotion container with the given id.
 *
 * @callback PromotionContainerByIdSelector
 *
 * @param {string} containerId - Container id.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.Promotions.types.PromotionContainer | undefined} Promotion container or undefined if none exists for the id.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectContainerById = (containerId, state) => {
    return selectContainers(state)[containerId];
};

/**
 * Selects promotions loading state.
 *
 * @callback PromotionsSelector
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.Common.types.CONTENT_STATE} Promotions list.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectLoadingState = state => {
    return selectState(state).loadingState;
};

/**
 * Selects promotion container loading state.
 *
 * @callback PromotionContainersLoadingStateSelector
 *
 * @param {string} containerId - Container id.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.Common.types.CONTENT_STATE} Promotion container loading state.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectContainersLoadingState = (containerId, state) => {
    return selectState(state).containersLoadingState[containerId] || UNKNOWN;
};

/**
 * Selects promotions by location id and applicable page.
 *
 * @callback PromotionsSelector
 *
 * @param {string} locationId - Location id.
 * @param {string} page - Page name.
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<object>} Promotions list.
 * @memberof Mojito.Services.Promotions.selectors
 */
export const selectPromotionsByLocationId = (locationId, page, state) => {
    const promotions = selectPromotions(state)[locationId];
    if (!promotions) {
        return EMPTY_ARRAY;
    }
    return promotions
        .filter(promotion => !page || promotion.applicablePages?.includes(page))
        .sort((a, b) => {
            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB);
        })
        .sort((a, b) => a.displayOrder - b.displayOrder);
};
